import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BadgeCheck, XCircle, Loader2, ShieldX, Linkedin } from "lucide-react";
import { linkedInAddUrl } from "@/lib/linkedin";
import { api } from "@/lib/api";

const Row = ({ label, value, testId }) => (
  <div className="flex justify-between gap-6 py-2.5 border-b border-slate-100 text-sm">
    <span className="text-slate-500">{label}</span><span className="text-slate-900 text-right font-medium" data-testid={testId}>{value || "—"}</span>
  </div>
);

export default function ZertifikatPruefenPage() {
  const { code } = useParams();
  const [d, setD] = useState(undefined);
  useEffect(() => { api.get(`/public/certificates/${encodeURIComponent(code)}`).then((r) => setD(r.data)).catch((e) => setD(e.response?.status === 404 ? null : "error")); }, [code]);
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50/40 flex items-center justify-center p-6" data-testid="cert-verify-page">
      <div className="w-full max-w-lg bg-white border border-slate-200 p-8 shadow-sm">
        {d === undefined && <div className="flex justify-center py-10"><Loader2 className="animate-spin text-slate-300" /></div>}
        {d === "error" && (
          <div className="text-center space-y-3 py-6" data-testid="cert-verify-error">
            <h1 className="font-heading text-2xl font-light tracking-tighter text-slate-900">Prüfung gerade nicht möglich</h1>
            <p className="text-sm text-slate-500">Bitte versuchen Sie es in einigen Minuten erneut.</p>
          </div>
        )}
        {d === null && (
          <div className="text-center space-y-3 py-6" data-testid="cert-verify-invalid">
            <XCircle size={44} className="mx-auto text-red-500" />
            <h1 className="font-heading text-2xl font-light tracking-tighter text-slate-900">Zertifikat nicht gefunden</h1>
            <p className="text-sm text-slate-500">Die Prüfnummer <b>{code}</b> ist nicht bekannt. Bitte prüfen Sie die Eingabe oder wenden Sie sich an den Bildungsträger.</p>
          </div>
        )}
        {d && d !== "error" && (
          <div className="space-y-6">
            {d.revoked_at ? (
              <div className="text-center space-y-2 border-2 border-red-200 bg-red-50 p-5" data-testid="cert-verify-revoked">
                <ShieldX size={48} className="mx-auto text-red-600" />
                <h1 className="font-heading text-3xl font-light tracking-tighter text-red-700">UNGÜLTIG</h1>
                <p className="text-sm text-red-700">Widerrufen am {new Date(d.revoked_at).toLocaleDateString("de-DE")}{d.revoked_reason ? ` – ${d.revoked_reason}` : ""}</p>
                <p className="text-xs text-red-600/80">Dieses Zertifikat wurde von {d.issuer || "dem Bildungsträger"} für ungültig erklärt.</p>
              </div>
            ) : (
              <div className="text-center space-y-2" data-testid="cert-verify-valid">
                <BadgeCheck size={48} className="mx-auto text-emerald-600" />
                <h1 className="font-heading text-3xl font-light tracking-tighter text-slate-900">Zertifikat gültig</h1>
                <p className="text-xs text-slate-500">Dieses Zertifikat wurde von <b>{d.issuer || "dem Bildungsträger"}</b> ausgestellt.</p>
              </div>
            )}
            <div>
              <Row label="Name" value={d.name} testId="cert-verify-name" />
              <Row label="Maßnahme" value={d.course} />
              <Row label="Modul" value={d.module} testId="cert-verify-module" />
              <Row label="Zeitraum" value={d.start && d.end ? `${d.start} – ${d.end}` : ""} />
              <Row label="Umfang" value={d.ue ? `${Number(d.ue).toLocaleString("de-DE")} Unterrichtseinheiten (UE)` : ""} testId="cert-verify-ue" />
              <Row label="Ausgestellt am" value={d.issued_on} />
              <Row label="Prüfnummer" value={d.code} />
            </div>
            {!d.revoked_at && (
              <a href={linkedInAddUrl({ name: d.course && d.course !== d.module ? `${d.course} – ${d.module}` : d.module, issuer: d.issuer, issuedOn: d.issued_on, code: d.code })} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-[#0a66c2] hover:bg-[#004182] text-white text-sm py-2.5 transition-colors" data-testid="cert-verify-linkedin">
                <Linkedin size={16} /> Zum LinkedIn-Profil hinzufügen
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
